'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { trackScrollDepth, isGAReady } from '../../lib/analytics';

/**
 * ScrollDepthTracker - Tracks scroll depth and fires analytics events
 * 
 * Tracks when users scroll to 10%, 20%, 30%, 40%, 50%, 60%, 70%, 80%, 90%, and 100% of the page
 * and sends events to Google Analytics.
 * 
 * Works with both Lenis smooth scroll and native scroll.
 */
export default function ScrollDepthTracker() {
  const pathname = usePathname();
  const trackedDepthsRef = useRef<Set<10 | 20 | 30 | 40 | 50 | 60 | 70 | 80 | 90 | 100>>(new Set());

  useEffect(() => {
    // Reset tracked depths when pathname changes
    trackedDepthsRef.current.clear();

    const handleScroll = (scrollTop?: number) => {
      if (!isGAReady()) {
        return;
      }

      // Calculate scroll depth percentage
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Use provided scrollTop (from Lenis) or fall back to window scroll
      const currentScrollTop = scrollTop !== undefined 
        ? scrollTop 
        : window.scrollY || document.documentElement.scrollTop;
      
      // Calculate percentage scrolled
      const scrollableHeight = documentHeight - windowHeight;
      const scrollPercentage = scrollableHeight > 0 
        ? Math.round((currentScrollTop / scrollableHeight) * 100)
        : 0;

      // Track milestones: 10%, 20%, 30%, 40%, 50%, 60%, 70%, 80%, 90%, 100%
      const milestones: (10 | 20 | 30 | 40 | 50 | 60 | 70 | 80 | 90 | 100)[] = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
      
      milestones.forEach((milestone) => {
        if (scrollPercentage >= milestone && !trackedDepthsRef.current.has(milestone)) {
          trackScrollDepth(milestone, pathname);
          trackedDepthsRef.current.add(milestone);
        }
      });
    };

    // Wait a bit for Lenis to initialize, then check if it's available
    let scrollTimeout: NodeJS.Timeout | null = null;
    let scrollHandler: ((e: { scroll: number }) => void) | null = null;
    let throttledHandleScroll: (() => void) | null = null;
    let lenisInstance: { scroll: number; on: (event: string, callback: (e: { scroll: number }) => void) => void; off?: (event: string, callback: (e: { scroll: number }) => void) => void } | null = null;

    const initTimeout = setTimeout(() => {
      const lenis = (window as unknown as { lenis?: { scroll: number; on: (event: string, callback: (e: { scroll: number }) => void) => void; off?: (event: string, callback: (e: { scroll: number }) => void) => void } }).lenis;
      lenisInstance = lenis || null;

      if (lenis) {
        // Use Lenis scroll events
        scrollHandler = (e: { scroll: number }) => {
          // Throttle Lenis scroll events
          if (scrollTimeout) {
            clearTimeout(scrollTimeout);
          }
          scrollTimeout = setTimeout(() => {
            handleScroll(e.scroll);
          }, 100);
        };
        
        lenis.on('scroll', scrollHandler);
        
        // Initial check with Lenis scroll position
        if (lenis.scroll !== undefined) {
          handleScroll(lenis.scroll);
        }
      } else {
        // Fallback to native scroll events
        throttledHandleScroll = () => {
          window.requestAnimationFrame(() => {
            handleScroll();
          });
        };

        // Initial check
        handleScroll();

        window.addEventListener('scroll', throttledHandleScroll, { passive: true });
      }
    }, 200);

    // Cleanup function
    return () => {
      clearTimeout(initTimeout);
      
      if (scrollHandler && lenisInstance?.off) {
        lenisInstance.off('scroll', scrollHandler);
      }
      
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      if (throttledHandleScroll) {
        window.removeEventListener('scroll', throttledHandleScroll);
      }
    };
  }, [pathname]);

  // This component doesn't render anything
  return null;
}

